import React, { useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { MapPin, Phone, Mail } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const LocationSection = () => {
  const { t } = useLanguage();
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  return (
    <section id="location" ref={ref} className="relative py-32 bg-[#F5F1E8]">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Number */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-9xl font-serif text-[#C9826B]/10 absolute top-10 left-6"
        >
          04
        </motion.div>

        <div className="relative z-10">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl md:text-6xl font-serif text-[#2A2520] mb-4">
              {t.location.title}
            </h2>
            <p className="text-xl text-[#C9826B] tracking-wider uppercase mb-6">
              {t.location.subtitle}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Text Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="space-y-8"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#C9826B]/10 flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-[#C9826B]" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-[#2A2520] mb-2">
                    {t.location.addressLabel}
                  </h3>
                  <p className="text-[#2A2520]/70">{t.location.address}</p>
                </div>
              </div>

              <p className="text-lg text-[#2A2520]/80 leading-relaxed">
                {t.location.description}
              </p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.6, duration: 0.6 }}
                className="bg-[#2C5F4F]/5 p-6 border-l-4 border-[#C9826B]"
              >
                <p className="text-[#2A2520] italic">
                  "{t.location.quote}"
                </p>
              </motion.div>
            </motion.div>

            {/* Map */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="h-[500px] rounded-none overflow-hidden shadow-2xl"
            >
              <iframe
                src="https://www.google.com/maps?q=Ca'+del+Sole+Italia,+Via+Mure+Sud+8,+Montagnana+PD&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Ca' del Sole Italia - Via Mure Sud 8, Montagnana"
              />
            </motion.div>
          </div>
        </div>
      </div>

      {/* Decorative Line */}
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#C9826B] to-transparent" />
    </section>
  );
};

export default LocationSection;
